import { SerializedContent } from '@/features/screenplay/components/editor/utils/JsonUtils'
import { V2SElement } from '@/pages/V2SPage'
import { Character } from '@storyverse/shared'
import { Dexie, Table, type EntityTable } from 'dexie'

// Change ID to string for UUIDs
export type DBEntity = { id: string }

export type Project = {
    title: string
    created_at: Date
    updated_at: Date
    genre: string
    subGenre?: string | null
    userId: string
}
export type LocalUser = {
    uid: string
    displayName: string | undefined
    email: string | null
    photoURL?: string
    name?: string
    filmmakingExpertise?: string
    onboardingCompleted?: boolean
}
export type Screenplay = {
    content: SerializedContent[]
    title: string
    projectId: string
    userId: string
    created_at: Date
    updated_at: Date
}
export type Story = {
    content: SerializedContent[]
    title: string
    projectId: string
    userId: string
    created_at: Date
    updated_at: Date
}

// Sync Queue Types
export type SyncStatus = 'PENDING' | 'SYNCING' | 'Synced' | 'FAILED'
export type SyncOperation = 'CREATE' | 'UPDATE' | 'DELETE'

export type SyncQueueItem = {
    entityTable: string
    entityId: string
    operation: SyncOperation
    payload: any
    status: SyncStatus
    retryCount: number
    error?: string
    created_at: Date
    updated_at: Date
}

export type SyncMetadata = {
    userId: string
    status: 'idle' | 'syncing' | 'synced' | 'error'
    lastHydratedAt?: Date
    error?: string
}

export type UserEntity = LocalUser
export type ProjectEntity = DBEntity & Project
export type CharacterEntity = Character & DBEntity
export type StoryEntity = Story & DBEntity
export type ScreenplayEntity = Screenplay & DBEntity
export type V2SElementEntity = V2SElement & DBEntity
export type ShotListEntity = ShotListDB & DBEntity
export type StoryboardEntity = StoryboardDB & DBEntity
export type SyncQueueEntity = SyncQueueItem & DBEntity
export type SyncMetadataEntity = SyncMetadata

export const db = new Dexie('StroyverseDB') as Dexie & {
    users: EntityTable<UserEntity, 'uid'>
    project: EntityTable<ProjectEntity, 'id'>
    characters: EntityTable<CharacterEntity, 'id'>
    stories: EntityTable<StoryEntity, 'id'>
    screenplays: EntityTable<ScreenplayEntity, 'id'>
    v2SElements: EntityTable<V2SElementEntity, 'id'>
    shotlists: EntityTable<ShotListEntity, 'id'>
    storyboards: EntityTable<StoryboardEntity, 'id'>
    sync_queue: EntityTable<SyncQueueEntity, 'id'>
    sync_metadata: EntityTable<SyncMetadataEntity, 'userId'>
}

// Version 7: Track per-user hydration state for offline-first sync
db.version(7).stores({
    users: 'uid,displayName,email,name,filmmakingExpertise,onboardingCompleted',
    project: 'id,title,genre,subGenre,userId,created_at,updated_at',
    stories: 'id,title,projectId,created_at,updated_at,[projectId+title]',
    characters: 'id, fullname, projectId',
    screenplays: 'id, title,projectId,created_at,updated_at,[projectId+title]',
    v2SElements: 'id,projectId,created_at,updated_at',
    shotlists: 'id,projectId,sceneId,created_at,updated_at,[projectId+sceneId]',
    storyboards: 'id,projectId,sceneId,created_at,updated_at,[projectId+sceneId]',
    sync_queue: 'id, entityTable, entityId, status, created_at',
    sync_metadata: 'userId,status,lastHydratedAt'
})

export type ShotListDB = {
    projectId: string
    sceneId: string
    shots: any[] // using any[] to avoid circular dependency
    created_at: Date
    updated_at: Date
}

export type StoryboardDB = {
    projectId: string
    sceneId: string
    frames: any[] // using any[] to avoid circular dependency
    created_at: Date
    updated_at: Date
}

export async function getOrCreate<T>(
    table: Table<T, any>,
    matchFn: (table: Table<T, any>) => Promise<T | undefined>,
    createFn: (table: Table<T, any>) => Promise<T>
): Promise<T> {
    const existing = await matchFn(table)
    if (existing) return existing

    const created = await createFn(table)
    return created
}
